import { BehaviorSubject, combineLatest, filter, map, Observable } from 'rxjs';
import { Disposable } from '../../hooks/useConstant';
import type { DataCompartment, IAppStorage } from '@aesop-fables/scrinium';
import { ScriniumServices } from '@aesop-fables/scrinium';
import { inject, createServiceModule, Scopes } from '@aesop-fables/containr';
import { UserCompartments, userStorageKey } from '../../data/user';
import { UserRole } from '../../models/UserData';
import { authContextKey } from '../authentication';
import type { IAuthenticationContext } from '../authentication';
import { GroupCompartments, groupStorageKey } from '../../data/group';
import { GroupData } from '../../models/GroupData';
import { TeamCompartments, teamStorageKey } from '../../data/team';
import { TeamData } from '../../models/TeamData';
import { OrganizationCompartments, organizationStorageKey } from '../../data/organization';
import { OrganizationData } from '../../models/OrganizationData';
import { NavItemProps } from '../../components/SideNavDrawer';
import { GroupFeatureMapCompartments, groupFeatureMapStorageKey } from '../../data/groupFeatureMap';
import { GroupFeatureMapData } from '../../models/GroupFeatureMapData';
import { hasRole, RoleType } from '../../helpers/roleUtils';

export const drillDownWizardKey = 'services/drillDownWizard';

export interface PreNavSelectedTiers {
  groupId?: number;
  teamId?: number;
}

export enum AdminTierTypeEnum {
  Group = 'Group',
  Team = 'Team',
  Organization = 'Organization',
  Root = 'Root',
}

export const tierOrder: AdminTierTypeEnum[] = [
  AdminTierTypeEnum.Group,
  AdminTierTypeEnum.Team,
  AdminTierTypeEnum.Organization,
  AdminTierTypeEnum.Root,
];

/**
 * Maps drill-down navigation tier to the admin tier being configured.
 *
 * @example drillDownTierToAdminTier(Root) → Organization
 * @example drillDownTierToAdminTier(Organization) → Team
 * @example drillDownTierToAdminTier(Team) → Group
 */
export const drillDownTierToAdminTier = (
  tier: AdminTierTypeEnum,
): AdminTierTypeEnum | undefined => {
  const currentIndex = tierOrder.indexOf(tier);
  if (currentIndex <= 0) return undefined;
  return tierOrder[currentIndex - 1];
};

class DrillDownWizard implements Disposable {
  private initialized = new BehaviorSubject<boolean>(false);
  private origin = new BehaviorSubject<AdminTierTypeEnum | undefined>(undefined);
  private highestAccess = new BehaviorSubject<AdminTierTypeEnum | undefined>(undefined);
  private highestRevenueAccess = new BehaviorSubject<AdminTierTypeEnum | undefined>(undefined);
  private tier = new BehaviorSubject<AdminTierTypeEnum | undefined>(undefined);
  private sidebarItems = new BehaviorSubject<NavItemProps[] | undefined>(undefined);
  private preNavSelectedTiers: PreNavSelectedTiers | null = null;
  private homeRedirectRoute: string | null = null;

  constructor(
    @inject(authContextKey)
    private readonly authContext: IAuthenticationContext,
    @inject(ScriniumServices.AppStorage)
    private readonly appStorage: IAppStorage,
  ) {
    const userDataCache = this.appStorage.retrieve<UserCompartments>(userStorageKey);
    const groupDataCache = this.appStorage.retrieve<GroupCompartments>(groupStorageKey);
    const groupFeatureMapDataCache =
      this.appStorage.retrieve<GroupFeatureMapCompartments>(groupFeatureMapStorageKey);
    const teamDataCache = this.appStorage.retrieve<TeamCompartments>(teamStorageKey);
    const orgDataCache = this.appStorage.retrieve<OrganizationCompartments>(organizationStorageKey);

    combineLatest([
      userDataCache.findCompartment('userRoles').initialized$,
      groupDataCache.initialized$,
      groupFeatureMapDataCache.initialized$,
      teamDataCache.findCompartment('team').initialized$,
      teamDataCache.findCompartment('allTeams').initialized$,
      orgDataCache.findCompartment('organization').initialized$,
      orgDataCache.findCompartment('allOrganizations').initialized$,
    ])
      .pipe(
        filter(
          ([userInit, groupInit, , teamInit, , orgInit, ,]) =>
            userInit && groupInit && teamInit && orgInit,
        ),
      )
      .subscribe(async ([userInit, groupInit, , teamInit, , orgInit, ,]) => {
        if (userInit && groupInit && teamInit && orgInit) {
          this.initialized.next(false);
          this.init();
        }
      });
  }

  async init() {
    try {
      const userDataCache = this.appStorage.retrieve<UserCompartments>(userStorageKey);
      const groupDataCache = this.appStorage.retrieve<GroupCompartments>(groupStorageKey);
      const groupFeatureMapDataCache =
        this.appStorage.retrieve<GroupFeatureMapCompartments>(groupFeatureMapStorageKey);
      const teamDataCache = this.appStorage.retrieve<TeamCompartments>(teamStorageKey);
      const orgDataCache =
        this.appStorage.retrieve<OrganizationCompartments>(organizationStorageKey);

      const userRoles = (
        userDataCache.findCompartment('userRoles') as DataCompartment<UserRole[]>
      ).getData() as UserRole[];
      const groupFeatureMap = (
        groupFeatureMapDataCache.findCompartment(
          'groupFeatureMap',
        ) as DataCompartment<GroupFeatureMapData>
      ).getData() as GroupFeatureMapData;

      const allGroups = (
        groupDataCache.findCompartment('allGroups') as DataCompartment<GroupData[]>
      ).getData() as GroupData[];
      const allTeams = (
        teamDataCache.findCompartment('allTeams') as DataCompartment<TeamData[]>
      ).getData() as TeamData[];
      const allOrgs = (
        orgDataCache.findCompartment('allOrganizations') as DataCompartment<OrganizationData[]>
      ).getData() as OrganizationData[];

      const numGroups = allGroups?.length ?? 0;
      const numTeams = allTeams?.length ?? 0;
      const numOrgs = allOrgs?.length ?? 0;

      const selectedGroup = (
        groupDataCache.findCompartment('group') as DataCompartment<GroupData>
      ).getData() as GroupData;
      const selectedTeam = (
        teamDataCache.findCompartment('team') as DataCompartment<TeamData>
      ).getData() as TeamData;
      const selectedOrg = (
        orgDataCache.findCompartment('organization') as DataCompartment<OrganizationData>
      ).getData() as OrganizationData;

      const highestAccess = this.findHighestRoleAccess(
        userRoles,
        selectedGroup,
        selectedTeam,
        selectedOrg,
      );
      const highestRevenueAccess = this.findHighestRoleAccess(
        userRoles,
        selectedGroup,
        selectedTeam,
        selectedOrg,
        'REVENUE',
      );

      this.highestAccess.next(highestAccess);
      this.highestRevenueAccess.next(highestRevenueAccess);

      if (numGroups >= 2) {
        this.origin.next(AdminTierTypeEnum.Group);
      } else if (numTeams >= 2 && highestAccess !== AdminTierTypeEnum.Group) {
        this.origin.next(AdminTierTypeEnum.Team);
      } else if (
        numOrgs >= 2 &&
        highestAccess !== AdminTierTypeEnum.Group &&
        highestAccess !== AdminTierTypeEnum.Team
      ) {
        this.origin.next(AdminTierTypeEnum.Organization);
      }

      if (groupFeatureMap?.anyoneCanCreateOrgs) {
        this.tier.next(undefined);
      } else if (!selectedGroup) {
        this.tier.next(AdminTierTypeEnum.Group);
      } else if (!selectedTeam) {
        this.tier.next(AdminTierTypeEnum.Team);
      } else if (!selectedOrg) {
        this.tier.next(AdminTierTypeEnum.Organization);
      } else {
        this.tier.next(AdminTierTypeEnum.Root);
      }

      this.initialized.next(true);
    } catch (err) {
      console.error(err);
    }
  }

  get initialized$(): Observable<boolean> {
    return this.initialized;
  }

  get origin$(): Observable<AdminTierTypeEnum | undefined> {
    return this.origin;
  }

  get highestAccess$(): Observable<AdminTierTypeEnum | undefined> {
    return this.highestAccess;
  }

  get highestRevenueAccess$(): Observable<AdminTierTypeEnum | undefined> {
    return this.highestRevenueAccess;
  }

  get tier$(): Observable<AdminTierTypeEnum | undefined> {
    return this.tier;
  }

  get sidebarItems$(): Observable<NavItemProps[] | undefined> {
    return this.sidebarItems;
  }

  private findHighestRoleAccess(
    roles: UserRole[],
    selectedGroup: GroupData,
    selectedTeam: TeamData,
    selectedOrg: OrganizationData,
    roleType?: 'REVENUE' | 'REPORTING',
  ): AdminTierTypeEnum | undefined {
    const roleTypeByPrefix: Record<'REVENUE' | 'REPORTING', RoleType> = {
      REVENUE: 'REVENUE_ADMIN',
      REPORTING: 'REPORTING_ADMIN',
    };
    const type: RoleType = roleType ? roleTypeByPrefix[roleType] : 'ADMIN';

    if (hasRole(roles, 'GROUP', type, selectedGroup?.id)) {
      return AdminTierTypeEnum.Group;
    }

    if (hasRole(roles, 'TEAM', type, selectedTeam?.id)) {
      return AdminTierTypeEnum.Team;
    }

    if (hasRole(roles, 'ORGANIZATION', type, selectedOrg?.id)) {
      return AdminTierTypeEnum.Organization;
    }

    return undefined;
  }

  findGroupNameById(): string {
    const groupDataCache = this.appStorage.retrieve<GroupCompartments>(groupStorageKey);
    const teamDataCache = this.appStorage.retrieve<TeamCompartments>(teamStorageKey);

    const allGroups = (
      groupDataCache.findCompartment('allGroups') as DataCompartment<GroupData[]>
    ).getData() as GroupData[];
    const team = (
      teamDataCache.findCompartment('team') as DataCompartment<TeamData>
    ).getData() as TeamData;

    let groupId = 0;
    if (this.highestAccess.pipe(map(x => x === AdminTierTypeEnum.Team))) {
      groupId = team?.groupId ?? 0;
    }
    // TODO b/e may possibly return groupId in org
    //  else if (this.highestAccess.pipe(map((x) => x === AdminTierTypeEnum.Organization))) {
    //   groupId = org?.groupId ?? 0
    // }

    const targetGroup = allGroups?.find(group => group.id === groupId);
    return targetGroup?.name ?? '';
  }

  setSidebarItems(items: NavItemProps[]) {
    this.sidebarItems.next(items);
  }

  setPreNavSelectedTiers(): void {
    const groupDataCache = this.appStorage.retrieve<GroupCompartments>(groupStorageKey);
    const teamDataCache = this.appStorage.retrieve<TeamCompartments>(teamStorageKey);
    const currentGroupId = (
      (groupDataCache.findCompartment('group') as DataCompartment<GroupData>).getData() as GroupData
    )?.id;
    const currentTeamId = (
      (teamDataCache.findCompartment('team') as DataCompartment<TeamData>).getData() as TeamData
    )?.id;

    this.preNavSelectedTiers =
      currentGroupId || currentTeamId ? { groupId: currentGroupId, teamId: currentTeamId } : null;
  }

  popPreNavSelectedTiers(): PreNavSelectedTiers | null {
    const state = this.preNavSelectedTiers;
    this.preNavSelectedTiers = null;
    return state;
  }

  markNavigatedHome(route: string): void {
    this.homeRedirectRoute = route;
  }

  consumeNavigatedHome(): string | null {
    const route = this.homeRedirectRoute;
    this.homeRedirectRoute = null;
    return route;
  }

  peekNavigatedHome(): string | null {
    return this.homeRedirectRoute;
  }

  reset() {
    this.initialized.next(false);
    this.origin.next(undefined);
    this.highestAccess.next(undefined);
    this.highestRevenueAccess.next(undefined);
    this.tier.next(undefined);
    this.sidebarItems.next(undefined);
    this.preNavSelectedTiers = null;
    this.homeRedirectRoute = null;
  }

  dispose(): void {
    console.log('disposing DrillDownWizard');
  }
}

export const withDrillDownWizard = createServiceModule(drillDownWizardKey, services => {
  services.autoResolve<DrillDownWizard>(drillDownWizardKey, DrillDownWizard, Scopes.Singleton);
});

export { DrillDownWizard };
